import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

import { Action, PartActionType, CreateBulkPartActionsRequest } from '../../../../../@types/partAction.type';
import { PartTypeActionLiveSearchItem } from '../../../../../@types/partType.type';
import { PartActionSubmitInput } from '../../useServiceDialogs';
import { PartActionSearchField } from './PartActionSearchField';
import { ExceptionalPartForm, ExceptionalPartData } from './ExceptionalPartForm';

export interface PartActionDialogProps {
  open: boolean;
  onClose: () => void;
  serviceId: number;
  clientId: number;
  contractId: number;
  clientContractIds: number[];
  locationId: number;
  canCreateExceptional: boolean;
  onSubmit: (input: PartActionSubmitInput) => void;
  submitting: boolean;
}

const PRIMARY_ACTIONS = [
  { code: PartActionType.INSTALL, label: 'Install' },
  { code: PartActionType.REPLACE, label: 'Replace' },
  { code: PartActionType.REMOVE, label: 'Remove' },
];

const OTHER_ACTIONS = [
  { code: PartActionType.DOA, label: 'DOA' },
  { code: PartActionType.CLEAN, label: 'Clean' },
  { code: PartActionType.TEST, label: 'Test' },
  { code: PartActionType.REPAIR, label: 'Repair' },
  { code: PartActionType.ADJUST, label: 'Adjust' },
];

const IN_PART_ACTIONS = new Set([PartActionType.INSTALL, PartActionType.REPLACE, PartActionType.DOA, PartActionType.CLEAN, PartActionType.TEST, PartActionType.REPAIR, PartActionType.ADJUST]);
const OUT_PART_ACTIONS = new Set([PartActionType.REMOVE, PartActionType.REPLACE, PartActionType.DOA]);

const buildPartData = (part: PartTypeActionLiveSearchItem | null) => {
  if (!part) return {};
  return {
    partTypeId: part.partTypeId, name: part.partTypeName || part.name,
    serial: part.serial1 || '', partId: part.partId || 0, ns_partId: part.ns_partId || 0,
    category: part.categoryCode || '', condition: part.conditionCode || '',
    location: part.locationName || '', locationId: part.locationId || 0, qty: 1,
    sw: part.softwareVersion || '', hw: part.hardwareVersion || '',
    fw: part.firmwareVersion || '', ft: part.fleetTagName || '',
  };
};

const buildExceptionalPartData = (data: ExceptionalPartData | null) => {
  if (!data || !data.partTypeId) return {};
  return {
    partTypeId: data.partTypeId, name: data.partTypeName, serial: data.serial,
    category: data.categoryCode, qty: 1, isExceptionalCreation: true,
  };
};

export const PartActionDialog: React.FC<PartActionDialogProps> = ({
  open, onClose, serviceId, clientId, contractId, clientContractIds, locationId,
  canCreateExceptional, onSubmit, submitting,
}) => {
  const theme = useTheme();
  const [actionType, setActionType] = useState<PartActionType | ''>('');
  const [inPart, setInPart] = useState<PartTypeActionLiveSearchItem | null>(null);
  const [outPart, setOutPart] = useState<PartTypeActionLiveSearchItem | null>(null);
  const [notes, setNotes] = useState('');
  const [useExceptional, setUseExceptional] = useState(false);
  const [exceptionalData, setExceptionalData] = useState<ExceptionalPartData | null>(null);

  useEffect(() => {
    if (open) {
      setActionType(''); setInPart(null); setOutPart(null);
      setNotes(''); setUseExceptional(false); setExceptionalData(null);
    }
  }, [open]);

  const needsIn = IN_PART_ACTIONS.has(actionType as PartActionType);
  const needsOut = OUT_PART_ACTIONS.has(actionType as PartActionType);

  const handleSubmit = () => {
    const isExceptional = useExceptional && exceptionalData?.partTypeId && exceptionalData?.serial;
    const inData = isExceptional ? buildExceptionalPartData(exceptionalData) : buildPartData(inPart);
    const outData = buildPartData(outPart);
    const action: CreateBulkPartActionsRequest = {
      id: Date.now(), execute: true, actionTypeCode: actionType, serviceId,
      installedOrMaintainedPartData: inData as unknown as JSON,
      removedPartData: outData as unknown as JSON,
      notes, isExceptionalCreation: !!isExceptional,
    };

    const input: PartActionSubmitInput = { actions: [action] };
    if (isExceptional && exceptionalData) {
      input.exceptionalPart = {
        partTypeId: exceptionalData.partTypeId,
        serial1: exceptionalData.serial,
        conditionCode: 'good',
        serviceId,
        partActionType: actionType,
        imageData: [],
      };
    }
    onSubmit(input);
  };

  const hasValidPart = useExceptional
    ? !!(exceptionalData?.serial && exceptionalData?.partTypeId)
    : !!((!needsIn || inPart) || (!needsOut || outPart));
  const canSubmit = actionType && !submitting && hasValidPart;

  const optionSx = (active: boolean) => ({
    flex: 1, px: 1.5, py: 1.5, borderRadius: 1, cursor: 'pointer', textAlign: 'center' as const,
    border: '1px solid', fontSize: '13px', fontWeight: 600,
    borderColor: active ? theme.palette.v4.accent : theme.palette.v4.border,
    bgcolor: active ? theme.palette.v4.accent : 'transparent',
    color: active ? '#fff' : theme.palette.text.primary,
    '&:hover': { borderColor: theme.palette.v4.accent, opacity: 0.85 },
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 600, fontSize: '1rem', display: 'flex', alignItems: 'center', gap: 1 }}>
        Part Action
        {canCreateExceptional && actionType && needsIn && (
          <Chip
            label={useExceptional ? 'Exceptional Creation' : 'Use Exceptional Creation'}
            size="small"
            onClick={() => setUseExceptional(!useExceptional)}
            sx={{
              ml: 'auto', height: 22, fontSize: '10px', fontWeight: 600, cursor: 'pointer',
              bgcolor: useExceptional ? 'warning.main' : 'v4.tableEven',
              color: useExceptional ? 'common.white' : 'text.secondary',
            }}
          />
        )}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <Box>
            <Typography sx={{ fontSize: '11px', fontWeight: 600, color: 'text.secondary', mb: 0.75 }}>Action Type</Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 0.5 }}>
              {PRIMARY_ACTIONS.map((a) => (
                <Box key={a.code} sx={optionSx(actionType === a.code)} onClick={() => setActionType(a.code)}>{a.label}</Box>
              ))}
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {OTHER_ACTIONS.map((a) => (
                <Box key={a.code} sx={optionSx(actionType === a.code)} onClick={() => setActionType(a.code)}>{a.label}</Box>
              ))}
            </Box>
          </Box>

          {needsOut && (
            <PartActionSearchField
              label={needsIn ? 'Part Out (Remove)' : 'Part to Remove'}
              clientContractIds={clientContractIds} locationId={locationId}
              actionType={actionType} action={Action.OUT} onSelect={setOutPart}
            />
          )}

          {needsIn && !useExceptional && (
            <PartActionSearchField
              label={needsOut ? 'Part In (Install)' : 'Part'}
              clientContractIds={clientContractIds} locationId={locationId}
              actionType={actionType} action={Action.IN} onSelect={setInPart}
            />
          )}

          {needsIn && useExceptional && (
            <ExceptionalPartForm clientId={clientId} contractId={contractId} onDataChange={setExceptionalData} />
          )}

          {actionType && (
            <TextField
              size="small" label="Notes" multiline minRows={2} value={notes}
              onChange={(e) => setNotes(e.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { fontSize: '12px' } }}
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={!canSubmit}
          sx={{ textTransform: 'none', bgcolor: 'v4.accent', '&:hover': { bgcolor: 'v4.accent', opacity: 0.9 } }}>
          {submitting ? <CircularProgress size={16} sx={{ color: 'common.white' }} /> : 'Submit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
